// @flow
import React, { Component } from "react";
import PropTypes from "prop-types";
import { compose } from "recompose";
import Masonry from "react-masonry-component";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import * as actions from "../../actions";
import { withPaginated, withInfiniteScroll, withLoading } from "../hoc";
import type { State } from "../../types/store";
import type { Entities } from "../../types/entities";
import ThumbItem from "../thumbitem";
import "./masonry.css";

type Props = {
  entities: Entities,
  typologyById: Object,
  tagById?: Object,
  page: number,
  total: number,
  itemsPerPage: number,
  isLoading?: boolean,
  hasError?: boolean,
  editMode: boolean,
  selectedItem?: Object,
  onGetItems: Function,
  onGetNextPageForItems: Function,
  onSelectItem: Function,
  onTagClicked: Function,
  onRemoveTag: Function,
  onRemoveItem: Function
};

type LocalState = {
  hasScroll: boolean
};

class Masonrylist extends Component<Props, LocalState> {
  state = {
    hasScroll: false
  };

  handleLayoutComplete: Function;

  componentDidMount() {
    const { entities, onGetItems, selectedItem } = this.props;
    if (entities.all.length === 0) {
      onGetItems();
    }
    this.setModalStatus(selectedItem !== null);
  }

  componentWillReceiveProps(nextProps) {
    // se un item è selezionato aprirlo sulla modal
    this.setModalStatus(nextProps.selectedItem !== null);
  }

  setModalStatus(open: boolean) {
    if (document.body) document.body.classList.toggle("modal-open", open);
  }

  listHasScrollbar() {
    let _hasScroll = false;
    if (document.getElementsByClassName("masonry-wrapper")[0] !== undefined) {
      const mainHeight = window.innerHeight;
      const minH = document.getElementsByClassName("masonry-wrapper")[0]
        .offsetHeight;
      _hasScroll = mainHeight < minH;
    }
    if (_hasScroll !== this.state.hasScroll) {
      this.setState({
        hasScroll: _hasScroll
      });
    }
  }

  onPaginatedSearch = e => {
    const { page, onGetNextPageForItems } = this.props;
    // controllo che ci siano ancora item da richiedere
    if (this.hasMoreItemsToLoad()) {
      onGetNextPageForItems(page + 1);
    }
  };

  hasMoreItemsToLoad = () => {
    const { page, total, itemsPerPage } = this.props;
    // controllo che ci siano ancora item da richiedere
    return (page + 1) * itemsPerPage < total;
  };

  handleLayoutComplete = () => {
    // handleLayoutComplete = entities => {
    // console.log("handleLayoutComplete");
    this.listHasScrollbar();
  };

  handleOnSelectItem = (id, index) => {
    const { entities, onSelectItem } = this.props;
    const currentItem = entities.byId[id];

    onSelectItem(currentItem, index);
  };

  handleOnTagClicked = (tag: string) => {
    const { onTagClicked } = this.props;
    onTagClicked([tag]);
  };

  render() {
    const {
      entities,
      typologyById,
      tagById,
      page,
      isLoading,
      hasError,
      editMode,
      onRemoveTag,
      onRemoveItem
    } = this.props;
    return (
      <div className="masonry-wrapper">
        <AdvancedList
          list={entities.all}
          byId={entities.byId}
          typologyById={typologyById}
          hasError={hasError}
          isLoading={isLoading}
          page={page}
          tagById={tagById}
          hasMoreItemsToLoad={this.hasMoreItemsToLoad()}
          hasScroll={this.state.hasScroll}
          onPaginatedSearch={this.onPaginatedSearch}
          onLayoutComplete={this.handleLayoutComplete}
          onItemSelect={this.handleOnSelectItem}
          onTagClicked={this.handleOnTagClicked}
          onRemoveTag={onRemoveTag}
          onRemoveItem={onRemoveItem}
          editMode={editMode}
        />
      </div>
    );
  }
}

const List = ({
  list,
  byId,
  typologyById,
  tagById,
  onLayoutComplete,
  onItemSelect,
  onTagClicked,
  onRemoveTag,
  onRemoveItem,
  editMode
}) => {
  return (
    <Masonry className="masonry" onLayoutComplete={onLayoutComplete}>
      {list.map((itemId, index) => {
        const item = byId[itemId];
        const icon = typologyById[item.typeID[0]].icon;
        item.icon = icon;

        return (
          <ThumbItem
            key={itemId}
            {...item}
            editMode={editMode}
            tagById={tagById}
            onItemSelect={onItemSelect}
            onTagClicked={onTagClicked}
            onRemoveTag={onRemoveTag}
            onRemoveItem={onRemoveItem}
          />
        );
      })}
    </Masonry>
  );
};

const paginatedCondition = props => {
  return (
    props.page !== null &&
    !props.isLoading &&
    props.hasError &&
    props.hasMoreItemsToLoad
  );
};

const infiniteScrollCondition = props =>
  window.innerHeight + window.scrollY >=
    document.getElementsByClassName("main-content")[0].offsetHeight - 300 &&
  props.list.length &&
  !props.isLoading &&
  !props.hasError;

const loadingCondition = props => props.isLoading;

const AdvancedList = compose(
  withPaginated(paginatedCondition),
  withInfiniteScroll(infiniteScrollCondition),
  withLoading(loadingCondition)
)(List);

function mapStateToProps(state: State) {
  const {
    entities,
    page,
    total,
    isLoading,
    hasError,
    selectedItem,
    itemsPerPage,
    editMode
  } = state.items;
  const { byId: typologyById } = state.filters.mainFilters.typologies.entities;
  const { byId: tagById } = state.filters.byTypology.tags.entities;
  return {
    entities,
    typologyById,
    tagById,
    page,
    total,
    itemsPerPage,
    isLoading,
    hasError,
    selectedItem,
    editMode
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onGetItems: bindActionCreators(actions.searchItems, dispatch),
    onGetNextPageForItems: bindActionCreators(
      actions.getNextPageForItems,
      dispatch
    ),
    onSelectItem: bindActionCreators(actions.selectItem, dispatch),
    onTagClicked: bindActionCreators(actions.setTags, dispatch),
    onRemoveTag: bindActionCreators(actions.removeTagFromItem, dispatch),
    onRemoveItem: bindActionCreators(actions.unpublishItem, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Masonrylist);

Masonrylist.propTypes = {
  entities: PropTypes.object.isRequired,
  typologyById: PropTypes.object.isRequired,
  tagById: PropTypes.object,
  page: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  editMode: PropTypes.bool,
  selectedItem: PropTypes.object,
  onGetItems: PropTypes.func.isRequired,
  onGetNextPageForItems: PropTypes.func.isRequired,
  onSelectItem: PropTypes.func.isRequired,
  onTagClicked: PropTypes.func.isRequired,
  onRemoveTag: PropTypes.func.isRequired,
  onRemoveItem: PropTypes.func.isRequired
};



// WEBPACK FOOTER //
// ./src/components/masonrylist/index.js